import { Component } from '@theme/component';
import { observerElement, resetSrcsetOnMobile, onIdleAfterPageLoad } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { DialogBeforeCloseEvent, DialogCloseEvent } from '@theme/events';

export class ShoppableGallery extends Component {
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;
  /** @type {((event: Event) => void) | null} */
  #handleDialogBeforeClose = null;
  /** @type {((event: Event) => void) | null} */
  #handleDialogClose = null;
  /** @type {Promise<void> | null} */
  #popupListPromise = null;

  connectedCallback() {
    super.connectedCallback();
    if (!Shopify.designMode) {
      onIdleAfterPageLoad(() => {
        this.#intersectionObserver = observerElement({
          element: this,
          callback: () => this.loadPopupList(),
          threshold: '300'
        });
      });
    }
    this.#handleDialogBeforeClose = (event) => this.#handleSiblingTransition(event);
    document.addEventListener(DialogBeforeCloseEvent.eventName, this.#handleDialogBeforeClose);
    this.#handleDialogClose = (event) => {
      const originalEvent = event.detail?.originalEvent;
      const isSiblingNavigation = originalEvent?.isSiblingPopup === true;
      const isFromShoppablePopup = event.target?.closest?.('.dialog-component');
      if (isFromShoppablePopup && isFromShoppablePopup.classList.contains('shoppable-card-dialog') && !isSiblingNavigation) {
        document.body.classList.remove('shoppable-popup-open');
      }
    };
    document.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver?.disconnect();

    if (this.#handleDialogBeforeClose) {
      document.removeEventListener(DialogBeforeCloseEvent.eventName, this.#handleDialogBeforeClose);
    }
    if (this.#handleDialogClose) {
      document.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    }
  }

  /**
   * Navigate to previous popup
   */
  async prevShoppablePopup(event) {
    await this.navigateToSiblingPopup(event, 'previous');
  }

  /**
   * Navigate to next popup
   */
  async nextShoppablePopup(event) {
    await this.navigateToSiblingPopup(event, 'next');
  }

  /**
   * Navigate to sibling popup (prev/next)
   * @param {Event} event - Click event
   * @param {string} direction - 'previous' or 'next'
   */
  async navigateToSiblingPopup(event, direction) {
    event?.preventDefault();
    event?.stopPropagation();

    const currentPopup = event.target.closest('dialog-component');
    if (!currentPopup) return;

    const siblingProperty = direction === 'previous'
      ? 'previousElementSibling'
      : 'nextElementSibling';

    const siblingPopup = currentPopup[siblingProperty];

    if (siblingPopup?.tagName === 'DIALOG-COMPONENT') {
      this.shoppablePopupList.classList.add('shoppable-popup-opening');
      this.shoppablePopupList.classList.add('shoppable-popup-skip-overlay');
      event.isSiblingPopup = true;
      currentPopup.closeDialog(event);

      const currentDeferredMedia = currentPopup.querySelector('deferred-media');
      const siblingDeferredMedia = siblingPopup.querySelector('deferred-media');

      if (currentDeferredMedia) {
        currentDeferredMedia.pauseMedia();
      }
      if (siblingDeferredMedia) {
        siblingDeferredMedia.playMedia();
      }

      await new Promise(resolve => setTimeout(resolve, 150));

      siblingPopup.openDialog();
      resetSrcsetOnMobile(siblingPopup);
      this.shoppablePopupList.classList.remove('shoppable-popup-opening');
    }
  }

  /**
   * Handle sibling popup transition - keep transition disabled for sibling transitions,
   * @param {Event} event - DialogBeforeCloseEvent
   */
  #handleSiblingTransition(event) {
    const originalEvent = event.detail?.originalEvent;
    const isSiblingPopup = originalEvent?.isSiblingPopup === true;
    const isSiblingNavigation = originalEvent?.isSiblingPopup === true;
    const isFromShoppablePopup = event.target?.closest?.('.dialog-component');
    if (isFromShoppablePopup && isFromShoppablePopup.classList.contains('shoppable-card-dialog') && !isSiblingNavigation) {
      this.shoppablePopupList.classList.remove('shoppable-popup-skip-overlay');
    }
  }

  /**
   * Lazy-loads the popup list once; callers share (and can await) the same
   * in-flight promise. Clears the cached promise if the load produced nothing,
   * so a later attempt can retry.
   * @returns {Promise<void>}
   */
  loadPopupList() {
    if (Shopify.designMode) return Promise.resolve();

    return (this.#popupListPromise ??= (async () => {
      const url = new URL('/', location.origin);
      url.searchParams.set('view', 'shoppable-loader');

      await sectionRenderer.fetchAndMorphContent({
        sectionId: this.sectionId,
        url: url,
        targetElement: this.shoppablePopupList,
        sourceSelector: '[ref="shoppablePopupList"]',
        cache: false,
        componentElement: this,
        checkLoadingStates: true
      });

      if (!this.shoppablePopupList?.children.length) {
        this.#popupListPromise = null;
        this.removeAttribute('data-loaded');
      }
    })());
  }

  get shoppablePopupList() {
    return this.refs.shoppablePopupList;
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }
}

if (!customElements.get('shoppable-gallery')) {
  customElements.define('shoppable-gallery', ShoppableGallery);
}

class ShoppableCard extends Component {
  /** @type {number | null} */
  #deselectTimeout = null;
  /** @type {number} */
  #startX = 0;
  /** @type {number} */
  #startY = 0;
  /** @type {boolean} */
  #isDragging = false;
  /** @type {number} */
  static #DRAG_THRESHOLD = 8; // px

  connectedCallback() {
    super.connectedCallback();

    // Track pointer events on component to detect drag vs click
    this.addEventListener('pointerdown', this.#handlePointerDown, { passive: true });
    this.addEventListener('pointermove', this.#handlePointerMove, { passive: true });
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);

    if (Shopify.designMode) {
      this.handleDesignMode();
    }
  }

  #handlePointerDown = (e) => {
    this.#startX = e.clientX;
    this.#startY = e.clientY;
    this.#isDragging = false;
    // Warm up the lazily-loaded popup the moment a card is pressed, so it is
    // usually ready by the time the click lands.
    this.closest('shoppable-gallery')?.loadPopupList?.();
  };

  #handlePointerMove = (e) => {
    const dx = Math.abs(e.clientX - this.#startX);
    const dy = Math.abs(e.clientY - this.#startY);
    if (dx > ShoppableCard.#DRAG_THRESHOLD || dy > ShoppableCard.#DRAG_THRESHOLD) {
      this.#isDragging = true;
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.removeEventListener('pointermove', this.#handlePointerMove);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeydown);

    if (this.handleBlockSelect) {
      this.removeEventListener('shopify:block:select', this.handleBlockSelect);
    }
    if (this.handleBlockDeselect) {
      this.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
    }
  }

  #handleKeydown = (event) => {
    // Handle Enter and Space keys for keyboard accessibility
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.#openDialog();
    }

  };

  #openDialog = async () => {
    const dialogId = this.getAttribute('data-dialog-id');
    if (!dialogId) return;

    let dialog = document.getElementById(dialogId);
    if (!dialog?.openDialog) {
      // Clicked before the lazy popup list finished loading — wait for it.
      const gallery = this.closest('shoppable-gallery');
      if (!gallery?.loadPopupList) return;
      gallery.setAttribute('aria-busy', 'true');
      await gallery.loadPopupList();
      gallery.removeAttribute('aria-busy');

      dialog = document.getElementById(dialogId);
      if (!dialog?.openDialog) return;
    }

    document.body.classList.add('shoppable-popup-open');
    dialog.openDialog();
    resetSrcsetOnMobile(dialog);
  };

  #handleClick = (event) => {
    if (this.#isDragging) {
      event?.preventDefault();
      event?.stopPropagation();
      // Reset dragging state after handling
      this.#isDragging = false;
      return;
    }

    event?.preventDefault();
    event?.stopPropagation();

    this.#openDialog();

    // Reset dragging state after handling
    this.#isDragging = false;
  }

  handleDesignMode() {
    this.handleBlockSelect = (event) => {
      if(event.target == this) {
        const dialogId = this.getAttribute('data-dialog-id');
        const dialog = document.getElementById(dialogId);
        if (dialog?.openDialog) {
          clearTimeout(this.#deselectTimeout);
          this.#deselectTimeout = null;
          dialog.openDialog();
        }
      }
    };
    this.addEventListener('shopify:block:select', this.handleBlockSelect);

    this.handleBlockDeselect = (event) => {
      if(event.target == this) {
        const dialogId = this.getAttribute('data-dialog-id');
        const dialog = document.getElementById(dialogId);
        if (dialog?.closeDialog) {
          this.#deselectTimeout = setTimeout(() => {
            dialog.closeDialog();
          }, 100);
        }
      }
    };
    this.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
  }
}

if (!customElements.get('shoppable-card')) {
  customElements.define('shoppable-card', ShoppableCard);
}